<script setup lang="ts">
import { computed } from 'vue';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IProfileSettingsGeneralSchema } from '@modules/profile-settings/submodules/general-settings/types';
import VLoader from '@components/loader/VLoader.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { useCurrentNamespace } from '@modules/permissions/composables';
import {
  PROFILE_SETTINGS_PERMISSIONS,
  TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS,
  TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS,
} from '@modules/profile-settings/constants';
import { useProfileSettingsMainSaveMutation } from '@modules/profile-settings/submodules/general-settings/mutations';
import { useUserSupervision } from '@modules/user/composables';

import { useAdminGeneralProfileSettings, useAffiliateGeneralProfileSettings } from './composables';

const { isAdminNamespace } = useCurrentNamespace();
const { isSupervision } = useUserSupervision();

const {
  adminSchema,
  isLoadingAdminData,
} = useAdminGeneralProfileSettings();

const {
  affiliateSchema,
  isLoadingAffiliateData,
} = useAffiliateGeneralProfileSettings();

const schema = computed<FormSchema<IProfileSettingsGeneralSchema>>(() => (
  isAdminNamespace.value ? adminSchema.value : affiliateSchema.value
));

const isLoading = computed<boolean>(() => (
  isAdminNamespace.value ? isLoadingAdminData.value : isLoadingAffiliateData.value
));

const translationGroup = computed(() => (
  isAdminNamespace.value ? TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS : TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS
));

const {
  mutateAsync,
  isLoading: isLoadingSave,
  clearError,
  errors,
} = useProfileSettingsMainSaveMutation();
</script>

<template>
  <VLoader v-data-qa-auto-id="'GeneralSettingsPage'"
    :loading="isLoading"
    min-height="200px"
  >
    <VForm
      :schema="schema"
      :readonly="!$hasPermission(PROFILE_SETTINGS_PERMISSIONS.EDIT) || isSupervision"
      :submit-btn-label="$getTranslation(translationGroup, 'SAVE')"
      :custom-errors="errors"
      :is-loading="isLoadingSave"
      @interacted="clearError"
      @submit="mutateAsync"
    />
  </VLoader>
</template>
